import { estRejet, montantsEnDevise, normaliserLibelle, type MontantEnDevise } from './libelle'
import {
  motsDuMarchand,
  referencesDuLibelle,
  type NatureOperation,
  type OperationBancaire,
} from './operations-bancaires'
import { contient, nomNormalise } from './pieces'
import { MOTS_GENERIQUES, motsProches } from './recuperation-rattachement'
import { sansAccents } from './texte'

// Rapprochement des justificatifs : quelle pièce de l'appli justifie quelle opération bancaire
// lue chez une source (plateforme du cabinet, banque), quelle que soit la source. Règle pure :
// rien de propre à une source ni au dépôt. Seule une correspondance stricte, que rien d'autre ne
// dispute dans les deux sens (paires concordantes, strictes ou non, sur toutes les opérations
// lues et toutes les pièces fournies), est « sûre » ; le reste est proposé à un humain ou attend
// sa pièce. Montants en centimes, jours AAAA-MM-JJ (Paris) ; écart positif quand l'opération
// suit la pièce.

// ---------------------------------------------------------------------------------------------
// Entrées
// ---------------------------------------------------------------------------------------------

/** Opération lue chez la source, à justifier par une pièce de l'appli. */
export type OperationAJustifier = OperationBancaire & {
  /** Identifiant opaque chez la source. */
  ref: string
  /** Justificatifs déjà posés chez la source, imputations comprises. */
  justificatifs: number
  /** Plusieurs imputations : jamais sûre. */
  ventilee: boolean
  /** Pas encore réalisée chez la source : jamais sûre. */
  provisoire: boolean
  /**
   * Validée chez la source par un humain (Dougs) ; absent quand la source ne le dit pas (Tiime).
   * Jamais sûre ; sans justificatif ni pièce en vue, plus rien n'est attendu (hors champ).
   */
  validee?: boolean
}

/** Pièce de l'appli qui peut justifier une opération : facture d'achat, ou avoir. */
export type PieceJustificative = {
  id: string
  entiteId: string
  /** Tiers émetteur rattaché, émetteur lu, et la plateforme qu'on paie (place de marché). */
  noms: readonly string[]
  /** Libellés bancaires du tiers émetteur (référentiel, écrits par un humain). */
  libellesBancaires: readonly string[]
  numero: string | null
  /** TTC et reste à payer, en centimes, sans zéro ni doublon ; négatifs pour un avoir. */
  montantsCts: readonly number[]
  /** Devise lue sur la pièce (code ISO), sinon null ; jamais une devise mise par défaut. */
  devise: string | null
  /** Date du document, sinon jour d'arrivée (AAAA-MM-JJ, Paris). */
  jour: string
  /** `jour` est une date lue sur la pièce ; sinon son seul jour d'arrivée : jamais stricte. */
  dateLue: boolean
  /** Dates des reçus doublons de la pièce. */
  joursPaiement: readonly string[]
  /** Échéance lue (prélèvement). */
  echeance: string | null
}

export type OptionsJustificatifs = {
  /** Opérations classées : de `decisionDu` à `au` ; les autres lues comptent pour l'unicité. */
  decisionDu: string
  au: string
  /** La lecture a atteint le début de la période lue ; sinon rien de sûr ni de reconnu. */
  complete: boolean
  /** Nos entités et nos personnes : un virement entre elles n'attend pas de pièce. */
  nomsInternes: readonly string[]
  /** Opération (ref) vers les pièces qu'un humain a écartées pour elle. */
  exclusions?: ReadonlyMap<string, readonly string[]>
}

// Recherche des pièces dans les mails : le port (IssueRechercheMails, ResultatRechercheMails,
// ChercheurPiecesMails) est défini dans recherche-pieces.ts, commun avec la recherche elle-même.

// ---------------------------------------------------------------------------------------------
// Constantes (toute modification incrémente VERSION_REGLE_JUSTIFICATIFS)
// ---------------------------------------------------------------------------------------------

/** Écart en jours, bornes comprises. */
export type FenetreJours = { readonly min: number; readonly max: number }
/** Bande de change, en millièmes : centimes d'euro pour 1 000 centimes de la devise. */
export type BorneChange = { readonly min: number; readonly max: number }

/** Écart où une pièce peut être candidate (propositions seulement). */
export const FENETRE_LARGE_JUSTIFICATIF: FenetreJours = { min: -15, max: 60 }

/** Écart où une correspondance peut être sûre, selon la nature de l'opération. */
export const FENETRES_SURES: Readonly<Record<NatureOperation, FenetreJours>> = {
  // Version 8 : de nouveau 3 jours avant la pièce. La semaine de la version 7 appariait au débit
  // du 12 le reçu du 17 d'un autre abonnement du même montant, payé ailleurs.
  carte: { min: -3, max: 7 },
  prelevement: { min: -3, max: 10 },
  virement: { min: -3, max: 21 },
  autre: { min: -3, max: 10 },
}

/** Pièce dans une autre devise que l'opération : même fenêtre sûre pour toute nature. */
export const FENETRE_SURE_DEVISE: FenetreJours = { min: -3, max: 7 }

/**
 * Écart où une paire concordante (même montant, fournisseur nommé au moins au sens large) compte
 * pour l'unicité, selon la nature de l'opération. La carte se paie à l'achat : sa fenêtre sûre.
 * Un prélèvement, un virement ou une autre opération peut régler la facture d'une période voisine
 * (paiement à 30 ou 45 jours d'un montant fixe) : toute la fenêtre large, pour que la facture de
 * la période suivante, plus proche en date, ne passe jamais pour la seule.
 */
export const FENETRES_CONCORDANCE: Readonly<Record<NatureOperation, FenetreJours>> = {
  carte: FENETRES_SURES.carte,
  prelevement: FENETRE_LARGE_JUSTIFICATIF,
  virement: FENETRE_LARGE_JUSTIFICATIF,
  autre: FENETRE_LARGE_JUSTIFICATIF,
}

/** Un prélèvement à 3 jours au plus de l'échéance lue est dans la fenêtre sûre. */
export const JOURS_ECHEANCE_SURE = 3

/**
 * Bandes de change pour une opération en euros : 0,83 à 0,94 € pour 1 $, frais compris. Le
 * dollar seulement ; une devise sans borne n'est jamais sûre. Servent aussi au rapprochement des
 * paiements en devise (`couvertDansLaDevise`) : y toucher change leur couverture. Version 10
 * (décision de Pierre-Marie du 31/10/2026) : la même bande que celle du débit
 * (`BORNES_CHANGE_DEBIT`), 0,85 laissait de côté des débits réels de 2026 à 0,844 et 0,845.
 */
export const BORNES_CHANGE: Readonly<Record<string, BorneChange>> = {
  USD: { min: 830, max: 940 },
}

/** Les bandes de change valent pour une opération dans cette devise. */
export const DEVISE_DES_BORNES = 'EUR'

/**
 * Montant proche (propositions seulement) : même devise à 2 % près, 2 € au moins ; ou devise
 * bornée hors de sa bande, mais à 2 % de ses bornes (de quoi voir le taux et régler la bande).
 */
export const PROCHE_JUSTIFICATIF: Readonly<{ minCts: number; millieme: number }> = {
  minCts: 200,
  millieme: 20,
}

/**
 * Poids des signaux : ordonnent les propositions, ne décident jamais. `fournisseur` quand un
 * nom concorde, `libelleBancaire` quand c'est le libellé bancaire du tiers ; `date` au plus,
 * décroissant avec l'écart jusqu'au bout de la fenêtre large.
 */
export const POIDS_JUSTIFICATIF: Readonly<{
  montant: number
  origine: number
  change: number
  proche: number
  fournisseur: number
  libelleBancaire: number
  numero: number
  echeance: number
  date: number
}> = {
  montant: 50,
  origine: 50,
  change: 30,
  proche: 20,
  fournisseur: 20,
  libelleBancaire: 25,
  numero: 25,
  echeance: 10,
  date: 15,
}

/** Score à atteindre pour être candidate. */
export const SEUIL_CANDIDATE = 40
/** Candidates gardées par opération. */
export const MAX_CANDIDATES = 5

/**
 * Libellés d'organismes qui n'envoient pas de facture : hors champ d'office. Version 3 (décision
 * de Pierre-Marie du 12/10/2026, après le premier passage à blanc) : la DGFIP écrite en toutes
 * lettres, comme Tiime la lit, et la retraite complémentaire de Malakoff Humanis (ses autres
 * contrats, prévoyance ou santé, peuvent avoir un avis : ils restent à fournir).
 */
export const LIBELLES_SANS_FACTURE: readonly string[] = [
  'URSSAF',
  'DGFIP',
  'DIRECTION GENERALE DES FINANCES PUBLIQUES',
  'IMPOTS',
  'TRESOR PUBLIC',
  'MALAKOFF HUMANIS RETRAITE',
]

/**
 * Références d'un paiement sans facture, lues dans le libellé (forme comparable, `nomNormalise`) :
 * la TVA déclarée sur une CA3 et prélevée par la DGFIP (« TVA1-082026-3310CA3 »), quel que soit le
 * nom du créancier (le SIE de la ville, la DGFIP en toutes lettres). Version 4 (décision de
 * Pierre-Marie du 12/10/2026, après le premier passage à blanc).
 */
export const REFERENCES_SANS_FACTURE: readonly RegExp[] = [/\bTVA\d?\s+\d{6}\s+3310CA3\b/]

/**
 * Prestataires qui déposent eux-mêmes leurs pièces sur l'interface du cabinet : on n'a jamais à les
 * charger, leurs prélèvements sont hors champ d'office, sans tâche ni écriture à rapprocher.
 * Version 10 (décision de Pierre-Marie du 31/10/2026).
 */
export const PRESTATAIRES_DEPOSANT_LEURS_PIECES: readonly string[] = ['NUMBR', 'APY PAIE']

/** Un numéro de pièce compacté compte à partir de 5 signes. */
export const SIGNES_MIN_NUMERO_JUSTIFICATIF = 5
/** La base d'un domaine (« NUAGE » de « NUAGE.IO ») compte à partir de 3 signes. */
export const SIGNES_MIN_BASE_DOMAINE = 3
/**
 * Au sens strict, les premiers mots distinctifs de l'opération et d'un nom de la pièce concordent
 * un à un, dans l'ordre, jusqu'à ce nombre (ni ville ni lieu, jamais un mot quelconque).
 */
export const PREMIERS_MOTS_FOURNISSEUR = 2

/**
 * Mots courants d'une raison sociale (« Cabinet », « Société », « Hôtel ») et formes sociales
 * étrangères : ils ne nomment personne au sens strict. En minuscules, sans accents ; en plus des
 * `MOTS_GENERIQUES`.
 */
export const MOTS_NON_DISTINCTIFS_JUSTIFICATIF: ReadonlySet<string> = new Set(
  (
    'cabinet cabinets societe societes ste agence agences atelier ateliers maison maisons ' +
    'compagnie compagnies cie etablissement etablissements ets hotel hotels studio studios ' +
    'parking parkings taxi taxis pty llp srl spa sprl bvba corporation company incorporated ' +
    // Version 7 : « OpCo » (operating company) d'un éditeur américain.
    'opco'
  ).split(' '),
)

/**
 * Intermédiaires de paiement en tête d'un libellé de carte (« PAYPAL *ACME ») : le marchand les
 * suit. (« SQ * », deux lettres, n'est déjà pas un mot.)
 */
export const INTERMEDIAIRES_PAIEMENT: ReadonlySet<string> = new Set([
  'paypal',
  'sumup',
  'stripe',
  'zettle',
  'izettle',
])

/** Un mot tronqué ne vaut, au sens large, qu'à partir de ce nombre de signes. */
const SIGNES_MIN_TRONQUE = 6

/** À incrémenter à tout changement de seuil, de fenêtre, de bande, de poids ou de liste. */
// Version 5 (12/10/2026) : une opération validée chez la source (Dougs) n'est jamais sûre ; sans
// justificatif ni pièce en vue, hors champ (`validee_sans_piece`).
// Version 6 (13/10/2026) : la fenêtre de décision est l'exercice en cours, et le précédent pendant
// ses cinq premiers mois (`fenetreJustificatifs`) : des opérations qu'aucun passage validé n'a vues.
// Version 7 (13/10/2026, décision de Pierre-Marie après l'audit des tâches) : un reçu de carte
// daté jusqu'à 7 jours après le débit est dans la fenêtre sûre (`FENETRES_SURES.carte`), et
// « OpCo » ne nomme personne (`MOTS_NON_DISTINCTIFS_JUSTIFICATIF`).
// Version 8 (13/10/2026, décision de Pierre-Marie après le passage à blanc du Chou-Marin) : la
// carte revient à 3 jours avant la pièce ; « OpCo » reste. Un reçu tardif se relie par le choix
// d'un humain (« C'est celle-ci »).
// Version 9 (31/10/2026, décision de Pierre-Marie) : la facture d'une autre entité, avancée par
// celle de la connexion avec sa ligne à refacturer de tout son montant, entre dans les candidates
// comme preuve de débit, déposée sans TVA (`nomPreuveDeDebit`) : passage à blanc à revalider.
// Version 10 (31/10/2026, décision de Pierre-Marie) : les prélèvements des prestataires qui déposent
// eux-mêmes leurs pièces sont hors champ (`PRESTATAIRES_DEPOSANT_LEURS_PIECES`) ; la bande du dollar
// descend à 0,83, celle du débit ; la ligne à refacturer d'une preuve de débit en dollars peut être
// en euros, dans cette bande : passage à blanc à revalider.
// Version 11 (31/10/2026, décision de Pierre-Marie) : le choix d'un humain porte sur toute facture
// de la frontière trouvée par la recherche, candidate ou non, opération à fournir comprise :
// passage à blanc à revalider.
export const VERSION_REGLE_JUSTIFICATIFS = 11

// ---------------------------------------------------------------------------------------------
// Sorties
// ---------------------------------------------------------------------------------------------

/** Signaux d'une paire, dans l'ordre où ils sont rendus. */
export const SIGNAUX_JUSTIFICATIF = [
  'montant',
  'origine',
  'change',
  'proche',
  'fournisseur',
  'numero',
  'echeance',
] as const
export type SignalJustificatif = (typeof SIGNAUX_JUSTIFICATIF)[number]

/** Pourquoi une opération est proposée plutôt que sûre : le premier critère qui manque. */
export const MOTIFS_PROPOSITION_JUSTIFICATIF = [
  'plusieurs_pieces',
  'piece_disputee',
  'fournisseur_non_nomme',
  'montant_different',
  'date_non_lue',
  'hors_fenetre_sure',
  'devise_non_lue',
  'devise_sans_borne',
  'ventilee',
  'provisoire',
  'validee',
  'lecture_incomplete',
  'avoir',
] as const
export type MotifPropositionJustificatif = (typeof MOTIFS_PROPOSITION_JUSTIFICATIF)[number]

/** Pourquoi aucune pièce n'est attendue. */
export const MOTIFS_HORS_CHAMP_JUSTIFICATIF = [
  'montant_nul',
  'rejet',
  'interne',
  'organisme',
  'depose_par_prestataire',
  'non_realisee',
  'validee_sans_piece',
  'credit',
] as const
export type MotifHorsChampJustificatif = (typeof MOTIFS_HORS_CHAMP_JUSTIFICATIF)[number]

export type MotifJustificatif = MotifPropositionJustificatif | MotifHorsChampJustificatif

export const LIBELLES_MOTIF_JUSTIFICATIF: Readonly<Record<MotifJustificatif, string>> = {
  plusieurs_pieces: 'Plusieurs pièces concordent',
  piece_disputee: 'La pièce concorde aussi avec une autre opération',
  fournisseur_non_nomme: 'Le libellé ne nomme pas sûrement le fournisseur',
  montant_different: 'Montant différent',
  date_non_lue: 'Date de la pièce non lue : seul son jour d’arrivée est connu',
  hors_fenetre_sure: 'Date trop éloignée de celle de la pièce',
  devise_non_lue: 'Devise de la pièce non lue',
  devise_sans_borne: 'Devise sans bande de change : jamais reliée seule',
  ventilee: 'Opération ventilée',
  provisoire: 'Opération pas encore réalisée',
  validee: 'Opération déjà validée chez le cabinet : jamais reliée seule',
  lecture_incomplete: 'Lecture des opérations incomplète',
  avoir: 'Avoir : jamais relié seul',
  montant_nul: 'Montant nul',
  rejet: 'Rejet ou impayé',
  interne: 'Virement entre nos entités ou nos personnes',
  organisme: 'Organisme sans facture (Urssaf, impôts, Trésor public, retraite)',
  depose_par_prestataire: 'Pièce déposée par le prestataire sur l’interface du cabinet',
  non_realisee: 'Opération pas encore réalisée, sans pièce en vue',
  validee_sans_piece: 'Validée chez le cabinet sans pièce, aucune pièce en vue',
  credit: 'Crédit sans avoir',
}

/** Une pièce candidate pour une opération. */
export type CandidateJustificatif = {
  pieceId: string
  score: number
  signaux: SignalJustificatif[]
  ecartJours: number
  /** Pièce en devise : taux effectif, centimes de l'opération pour 1 000 centimes de la pièce. */
  rapportChange: number | null
}

/** Une paire évaluée : ses signaux, et si elle est candidate ou stricte. */
export type EvaluationJustificatif = CandidateJustificatif & {
  /** La pièce peut être proposée pour l'opération. */
  arete: boolean
  /** Correspondance stricte : sûre si elle est seule dans les deux sens. */
  stricte: boolean
}

export type IssueJustificatif =
  | {
      issue: 'sure'
      pieceId: string
      signaux: SignalJustificatif[]
      ecartJours: number
      rapportChange: number | null
    }
  | { issue: 'justifiee'; pieceReconnue: string | null; candidates: CandidateJustificatif[] }
  | { issue: 'proposee'; candidates: CandidateJustificatif[]; motif: MotifPropositionJustificatif }
  | { issue: 'a_fournir' }
  | { issue: 'hors_champ'; motif: MotifHorsChampJustificatif }
  | { issue: 'hors_fenetre' }

export type ResultatJustificatifs = {
  /** Une issue par opération lue (ref), dans l'ordre des dates puis des références. */
  issues: Map<string, IssueJustificatif>
  /** Pièces datées de la fenêtre de décision sans aucune opération en vue, par date. */
  piecesSansOperation: string[]
}

// ---------------------------------------------------------------------------------------------
// Calculs de base
// ---------------------------------------------------------------------------------------------

/** Poids de la date pour un écart : 15 au même jour, 0 au-delà de la fenêtre large. */
export function poidsDateJustificatif(ecart: number): number {
  const portee = FENETRE_LARGE_JUSTIFICATIF.max + 1
  return Math.round(
    (POIDS_JUSTIFICATIF.date * (portee - Math.min(Math.abs(ecart), portee))) / portee,
  )
}

const entierPositif = (n: number) => Number.isSafeInteger(n) && n > 0

/**
 * Taux effectif en millièmes, arrondi au plus proche : centimes de l'opération pour 1 000
 * centimes de la pièce (0,89 € pour 1 $ donne 890). Calcul entier ; null si un montant manque.
 */
export function rapportDeChange(eurCts: number, devCts: number): number | null {
  if (!entierPositif(eurCts) || !entierPositif(devCts)) return null
  const eur = BigInt(eurCts)
  const dev = BigInt(devCts)
  return Number((2n * eur * 1000n + dev) / (2n * dev))
}

const bornesDe = (devise: string): BorneChange | null =>
  Object.hasOwn(BORNES_CHANGE, devise) ? (BORNES_CHANGE[devise] ?? null) : null

/**
 * Bande de change d'un montant en `devise` réglé par une opération en `deviseOperation` : une
 * opération en euros et une autre devise bornée seulement, sinon null.
 */
export function bornesDeChange(devise: string, deviseOperation: string): BorneChange | null {
  return deviseOperation === DEVISE_DES_BORNES && devise !== deviseOperation
    ? bornesDe(devise)
    : null
}

/** Montant en euros dans la bande d'un montant en devise, bornes comprises ; calcul entier. */
function dansLaBande(eurCts: number, devCts: number, b: BorneChange): boolean {
  if (!entierPositif(eurCts) || !entierPositif(devCts)) return false
  const eur = BigInt(eurCts) * 1000n
  const dev = BigInt(devCts)
  return eur >= dev * BigInt(b.min) && eur <= dev * BigInt(b.max)
}

/** Hors de la bande, mais à `PROCHE_JUSTIFICATIF.millieme` près de ses bornes. */
function presDeLaBande(eurCts: number, devCts: number, b: BorneChange): boolean {
  if (!entierPositif(eurCts) || !entierPositif(devCts)) return false
  const eur = BigInt(eurCts) * 1_000_000n
  const dev = BigInt(devCts)
  const marge = BigInt(PROCHE_JUSTIFICATIF.millieme)
  return (
    eur >= dev * BigInt(b.min) * (1000n - marge) && eur <= dev * BigInt(b.max) * (1000n + marge)
  )
}

/** Débit en euros dans la bande de change de la devise de la pièce (`BORNES_CHANGE`). */
export function changeDansLaBande(eurCts: number, devCts: number, devise: string): boolean {
  const b = bornesDe(devise)
  return b !== null && dansLaBande(eurCts, devCts, b)
}

/** Même devise : à 2 % près du montant de la pièce, 2 € au moins. */
function montantProche(opCts: number, pieceCts: number): boolean {
  const ecart = BigInt(Math.abs(opCts - pieceCts))
  return (
    ecart <= BigInt(PROCHE_JUSTIFICATIF.minCts) ||
    ecart * 1000n <= BigInt(pieceCts) * BigInt(PROCHE_JUSTIFICATIF.millieme)
  )
}

const JOUR = /^(\d{4})-(\d{2})-(\d{2})$/

/** Numéro du jour (jours depuis le 1er janvier 1970) d'un jour réel AAAA-MM-JJ, sinon null. */
export function numeroDuJour(jour: unknown): number | null {
  if (typeof jour !== 'string') return null
  const m = JOUR.exec(jour)
  if (!m) return null
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  if (Number.isNaN(t) || new Date(t).toISOString().slice(0, 10) !== jour) return null
  return t / 86_400_000
}

const dans = (e: number, f: FenetreJours) => e >= f.min && e <= f.max

const comparer = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

// ---------------------------------------------------------------------------------------------
// Fournisseur nommé
// ---------------------------------------------------------------------------------------------

type MotsOperation = {
  /** Mots qui nomment le marchand (ceux du marchand d'abord), en minuscules, sans génériques. */
  mots: readonly string[]
  /**
   * Tête de l'opération, au sens strict : les mots du marchand quand la source le donne (jamais
   * la suite du libellé, ville ou pays), sinon ceux du libellé ; sans mots courants d'une raison
   * sociale ni intermédiaire de paiement en tête.
   */
  tete: readonly string[]
  /** Tous les mots bruts, génériques compris : « azurtelecom » contre « azur » et « telecom ». */
  bruts: ReadonlySet<string>
  /** Marchand et libellé, comparables mot pour mot à un libellé bancaire du tiers. */
  bancaire: string
}

type MotsPiece = {
  mots: readonly string[]
  /** Chaque nom de la pièce, ses mots distinctifs au sens strict, dans l'ordre. */
  noms: readonly (readonly string[])[]
  bruts: ReadonlySet<string>
  /** Chaque nom en entier, compacté (« acmestudio ») : pour un libellé tronqué. */
  entiers: readonly string[]
  /** Libellés bancaires du tiers, normalisés. */
  libelles: readonly string[]
}

const distinctifs = (mots: readonly string[]) =>
  mots.map((m) => m.toLowerCase()).filter((m) => !MOTS_GENERIQUES.has(m))

const stricts = (mots: readonly string[]) =>
  distinctifs(mots).filter((m) => !MOTS_NON_DISTINCTIFS_JUSTIFICATIF.has(m))

const motsBruts = (texte: string) =>
  sansAccents(texte)
    .split(/[^a-z0-9]+/)
    .filter((m) => m.length > 0)

function motsOperation(op: Pick<OperationBancaire, 'libelle' | 'marchand'>): MotsOperation {
  const marchand = op.marchand?.trim() ? op.marchand : null
  let tete = stricts(motsDuMarchand({ libelle: marchand ?? op.libelle, marchand: null }))
  if (tete.length > 1 && INTERMEDIAIRES_PAIEMENT.has(tete[0]!)) tete = tete.slice(1)
  return {
    mots: distinctifs(motsDuMarchand(op)),
    tete,
    bruts: new Set([...motsBruts(op.marchand ?? ''), ...motsBruts(op.libelle)]),
    bancaire: nomNormalise(`${op.marchand ?? ''} ${op.libelle}`),
  }
}

function motsPiece(p: Pick<PieceJustificative, 'noms' | 'libellesBancaires'>): MotsPiece {
  const mots = p.noms.flatMap((nom) =>
    distinctifs(motsDuMarchand({ libelle: nom, marchand: null })),
  )
  return {
    mots: [...new Set(mots)],
    noms: p.noms.map((nom) => stricts(motsDuMarchand({ libelle: nom, marchand: null }))),
    bruts: new Set(p.noms.flatMap(motsBruts)),
    entiers: p.noms.map((nom) => motsBruts(nom).join('')).filter((n) => n !== ''),
    libelles: p.libellesBancaires.map(nomNormalise).filter((l) => l !== ''),
  }
}

/** Lettres et chiffres seuls : « nuage.io » et « nuageio » se valent. */
const compacte = (mot: string) => mot.replace(/[^a-z0-9]/g, '')

/** Base d'un nom de domaine (« nuage » de « app.nuage.io »), compactée ; null sinon. */
function baseDeDomaine(mot: string): string | null {
  const parties = mot.split('.')
  if (parties.length < 2) return null
  const base = compacte(parties[parties.length - 2] ?? '')
  return base.length >= SIGNES_MIN_BASE_DOMAINE ? base : null
}

function concordent(a: string, b: string, autres: () => ReadonlySet<string>): boolean {
  const ca = compacte(a)
  const cb = compacte(b)
  if (ca === cb) return true
  if (baseDeDomaine(a) === cb || baseDeDomaine(b) === ca) return true
  return motsProches(a, b, autres())
}

/**
 * `long` est `court` suivi ou précédé d'un mot du côté de `court` (« azurtelecom » : « azur » et
 * « telecom » d'« Azur Telecom ») ; jamais un mot simplement contenu dans un autre.
 */
function compose(long: string, court: string, motsDuCourt: ReadonlySet<string>): boolean {
  if (court.length < 4 || long.length <= court.length) return false
  if (long.startsWith(court)) return motsDuCourt.has(long.slice(court.length))
  if (long.endsWith(court)) return motsDuCourt.has(long.slice(0, long.length - court.length))
  return false
}

/** Deux mots se valent au sens strict : forme compacte, base d'un domaine, ou mot composé. */
function concordeStrict(
  a: string,
  b: string,
  brutsA: ReadonlySet<string>,
  brutsB: ReadonlySet<string>,
): boolean {
  const ca = compacte(a)
  const cb = compacte(b)
  if (ca === cb) return true
  const ba = baseDeDomaine(a) ?? ca
  const bb = baseDeDomaine(b) ?? cb
  if (ba === cb || bb === ca) return true
  return compose(ba, cb, brutsB) || compose(bb, ca, brutsA)
}

/**
 * La tête de l'opération nomme ce nom : leurs premiers mots distinctifs concordent un à un, dans
 * l'ordre, jusqu'à deux (ou les deux du nom sont collés en un mot). Un deuxième mot de
 * l'opération qui ne concorde pas avec celui du nom (autre commerce, ville, autre personne)
 * l'exclut ; une tête d'un seul mot (la marque, un libellé tronqué) suffit.
 */
function teteNomme(o: MotsOperation, nom: readonly string[], brutsNom: ReadonlySet<string>) {
  const t = o.tete
  const n = nom.slice(0, PREMIERS_MOTS_FOURNISSEUR)
  if (n.length === 0 || t.length === 0) return false
  const vaut = (a: string, b: string) => concordeStrict(a, b, o.bruts, brutsNom)
  if (n.length > 1 && vaut(t[0]!, n.map(compacte).join(''))) return true
  const k = Math.min(n.length, t.length)
  return n.slice(0, k).every((b, i) => vaut(t[i]!, b))
}

type Nommage = { strict: boolean; large: boolean; parLibelle: boolean }

function nommer(o: MotsOperation, p: MotsPiece): Nommage {
  const parLibelle = p.libelles.some((l) => contient(o.bancaire, l))
  const strict = parLibelle || p.noms.some((nom) => teteNomme(o, nom, p.bruts))
  let large = strict
  if (!large && p.mots.length > 0) {
    let autres: Set<string> | undefined
    const tous = () => (autres ??= new Set([...o.bruts, ...p.bruts]))
    large = o.mots.some((a) => p.mots.some((b) => concordent(a, b, tous)))
  }
  // Nom collé puis tronqué (« ACMESTU » d'Acme Studio) : au sens large seulement.
  if (!large)
    large = o.mots.some((a) => {
      const ca = compacte(a)
      return ca.length >= SIGNES_MIN_TRONQUE && p.entiers.some((n) => n.startsWith(ca))
    })
  return { parLibelle, strict, large }
}

/**
 * L'opération nomme-t-elle le fournisseur de la pièce ? Strict : un libellé bancaire du tiers
 * est contenu mot pour mot ; ou les premiers mots de la tête de l'opération (le marchand donné
 * par la source, sinon le libellé, hors mots génériques, mots courants d'une raison sociale et
 * intermédiaire de paiement) et d'un nom de la pièce concordent un à un, dans l'ordre, jusqu'à
 * deux : jamais un mot quelconque de l'opération, ni un deuxième mot qui diffère ; même forme
 * compacte, base d'un domaine ou mot composé, jamais un mot contenu dans un autre. Une ville qui
 * suit le marchand ne compte donc pas. Large : un mot de
 * l'opération concorde avec un mot d'un nom (mots proches compris), ou un mot tronqué de six
 * signes au moins ouvre un nom en entier.
 */
export function nommeLeFournisseur(
  op: Pick<OperationBancaire, 'libelle' | 'marchand'>,
  piece: Pick<PieceJustificative, 'noms' | 'libellesBancaires'>,
  mode: 'strict' | 'large',
): boolean {
  const n = nommer(motsOperation(op), motsPiece(piece))
  return mode === 'strict' ? n.strict : n.large
}

// ---------------------------------------------------------------------------------------------
// Paires (opération, pièce)
// ---------------------------------------------------------------------------------------------

type OperationPrete<O extends OperationBancaire> = MotsOperation & {
  op: O
  jour: number
  /** Références du libellé, compactées. */
  references: ReadonlySet<string>
  /** Montants en devise cités par le libellé (montant d'origine d'un paiement par carte). */
  origines: readonly MontantEnDevise[]
}

type PiecePrete = MotsPiece & {
  piece: PieceJustificative
  jour: number
  /** Date de la pièce puis celles de ses reçus, lisibles. */
  jours: readonly number[]
  echeance: number | null
  numero: string | null
  /** Montants non nuls, sans doublon, signés. */
  montants: readonly number[]
  /** Leurs valeurs absolues. */
  absolus: readonly number[]
  /** Facture : aucun montant négatif ; avoir : que des montants négatifs. */
  sens: 'facture' | 'avoir' | 'mixte'
  devise: string | null
}

const compacteMajuscules = (t: string) => normaliserLibelle(t).replace(/[^A-Z0-9]/g, '')

function preparerOperation<O extends OperationBancaire>(op: O): OperationPrete<O> | null {
  const jour = numeroDuJour(op.date)
  if (jour === null || !Number.isSafeInteger(op.montantCts)) return null
  return {
    ...motsOperation(op),
    op,
    jour,
    references: new Set(referencesDuLibelle(op.libelle).map(compacteMajuscules)),
    origines: montantsEnDevise(op.libelle),
  }
}

function preparerPiece(piece: PieceJustificative): PiecePrete | null {
  const jour = numeroDuJour(piece.jour)
  if (jour === null) return null
  const paiements = piece.joursPaiement.map(numeroDuJour).filter((j): j is number => j !== null)
  const montants = [...new Set(piece.montantsCts.filter((m) => Number.isSafeInteger(m) && m !== 0))]
  const numero = piece.numero ? compacteMajuscules(piece.numero) : ''
  const devise =
    typeof piece.devise === 'string' && /^[A-Z]{3}$/.test(piece.devise) ? piece.devise : null
  return {
    ...motsPiece(piece),
    piece,
    jour,
    jours: [jour, ...paiements],
    echeance: numeroDuJour(piece.echeance),
    numero: numero.length >= SIGNES_MIN_NUMERO_JUSTIFICATIF ? numero : null,
    montants,
    absolus: montants.map(Math.abs),
    sens: montants.every((m) => m > 0)
      ? 'facture'
      : montants.every((m) => m < 0)
        ? 'avoir'
        : 'mixte',
    devise,
  }
}

/** Le plus petit écart sur les jours de la pièce ; à égalité, l'opération qui suit la pièce. */
function ecartRetenu(jourOperation: number, jours: readonly number[]): number {
  let retenu = jourOperation - jours[0]!
  for (const j of jours) {
    const e = jourOperation - j
    if (Math.abs(e) < Math.abs(retenu) || (Math.abs(e) === Math.abs(retenu) && e > retenu))
      retenu = e
  }
  return retenu
}

const SIGNAUX_D_ARETE: ReadonlySet<SignalJustificatif> = new Set([
  'fournisseur',
  'numero',
  'montant',
  'origine',
  'change',
  'proche',
])

const fenetreDeNature = (n: NatureOperation) =>
  Object.hasOwn(FENETRES_SURES, n) ? FENETRES_SURES[n] : FENETRES_SURES.autre

const fenetreDeConcordance = (n: NatureOperation) =>
  Object.hasOwn(FENETRES_CONCORDANCE, n) ? FENETRES_CONCORDANCE[n] : FENETRES_CONCORDANCE.autre

/** Une paire évaluée, avec ce qu'il faut pour dire pourquoi elle n'est pas stricte. */
type Paire = EvaluationJustificatif & {
  nomStrict: boolean
  fenetreSure: boolean
  deviseLue: boolean
  dateLue: boolean
  /** Les montants se comparent : même devise, devise bornée, ou montant d'origine cité. */
  montantComparable: boolean
  /**
   * Le montant tel que la correspondance stricte l'exige : égal dans la même devise ; change dans
   * la bande pour une devise bornée ; montant d'origine pour une devise sans bande (jamais sûre).
   */
  montantConcorde: boolean
  /** Devise lue, autre que celle de l'opération, sans bande de change. */
  sansBorne: boolean
  /**
   * Compte pour l'unicité, stricte ou non : débit et facture, même montant (égal, change dans la
   * bande ou montant d'origine cité, devise présumée quand elle n'est pas lue), fournisseur nommé
   * au moins au sens large, écart dans la fenêtre de concordance de la nature.
   */
  concordante: boolean
  /** Départage des paires concordantes : échéance d'un prélèvement, numéro de la pièce. */
  decisive: boolean
}

/** Un débit contre une facture, un crédit contre un avoir. */
const signesCompatibles = (montantCts: number, sens: PiecePrete['sens']) =>
  (montantCts < 0 && sens === 'facture') || (montantCts > 0 && sens === 'avoir')

/** Jamais une arête, sans rien calculer de plus : autre entité, sens contraire, trop loin. */
function horsDePortee(o: OperationPrete<OperationBancaire>, p: PiecePrete): boolean {
  return (
    o.op.entiteId !== p.piece.entiteId ||
    !signesCompatibles(o.op.montantCts, p.sens) ||
    !dans(ecartRetenu(o.jour, p.jours), FENETRE_LARGE_JUSTIFICATIF)
  )
}

function evaluer(
  o: OperationPrete<OperationBancaire>,
  p: PiecePrete,
  bandes: Readonly<Record<string, BorneChange>> = BORNES_CHANGE,
): Paire | null {
  const op = o.op
  if (op.entiteId !== p.piece.entiteId) return null
  const e = ecartRetenu(o.jour, p.jours)
  const debit = op.montantCts < 0
  const compatibles = signesCompatibles(op.montantCts, p.sens)
  const absOp = Math.abs(op.montantCts)

  // Devise de la pièce : la même que l'opération, une autre (bornée ou non), ou non lue. Non
  // lue, elle est présumée la même ou une devise bornée, pour proposer seulement.
  const devise = p.devise
  const lue = devise !== null
  const etrangere = lue && devise !== op.devise
  const bornes =
    etrangere && op.devise === DEVISE_DES_BORNES && Object.hasOwn(bandes, devise)
      ? (bandes[devise] ?? null)
      : null
  const bornesEnJeu: readonly BorneChange[] = bornes
    ? [bornes]
    : !lue && op.devise === DEVISE_DES_BORNES
      ? Object.values(bandes)
      : []

  const montant = !etrangere && absOp > 0 && p.montants.includes(-op.montantCts)
  const origine =
    etrangere || !lue
      ? (o.origines.find(
          (m) =>
            (lue ? m.devise === devise : m.devise !== op.devise) &&
            p.absolus.includes(m.montantCts),
        )?.montantCts ?? null)
      : null
  const change = p.absolus.find((x) => bornesEnJeu.some((b) => dansLaBande(absOp, x, b))) ?? null
  let pres: number | null = null
  let proche = false
  if (!montant && origine === null && change === null && absOp > 0) {
    proche = !etrangere && p.absolus.some((x) => montantProche(absOp, x))
    if (!proche) {
      pres = p.absolus.find((x) => bornesEnJeu.some((b) => presDeLaBande(absOp, x, b))) ?? null
      proche = pres !== null
    }
  }
  const nommage = nommer(o, p)
  const numero = p.numero !== null && o.references.has(p.numero)
  const echeance =
    op.nature === 'prelevement' &&
    p.echeance !== null &&
    Math.abs(o.jour - p.echeance) <= JOURS_ECHEANCE_SURE

  const signaux: SignalJustificatif[] = []
  if (montant) signaux.push('montant')
  if (origine !== null) signaux.push('origine')
  if (change !== null) signaux.push('change')
  if (proche) signaux.push('proche')
  if (nommage.large) signaux.push('fournisseur')
  if (numero) signaux.push('numero')
  if (echeance) signaux.push('echeance')
  const score =
    signaux.reduce(
      (s, x) =>
        s +
        (x === 'fournisseur' && nommage.parLibelle
          ? POIDS_JUSTIFICATIF.libelleBancaire
          : POIDS_JUSTIFICATIF[x]),
      0,
    ) + poidsDateJustificatif(e)
  const arete =
    compatibles &&
    dans(e, FENETRE_LARGE_JUSTIFICATIF) &&
    score >= SEUIL_CANDIDATE &&
    signaux.some((s) => SIGNAUX_D_ARETE.has(s))

  const fenetreSure = etrangere
    ? dans(e, FENETRE_SURE_DEVISE)
    : dans(e, fenetreDeNature(op.nature)) || echeance
  // Une devise bornée exige toujours le change dans la bande : un montant d'origine cité ne
  // suffit pas (commission qui le recopie, débit hors bande).
  const montantConcorde = etrangere
    ? bornes !== null
      ? change !== null
      : origine !== null
    : montant
  const dateLue = p.piece.dateLue !== false
  const stricte =
    arete &&
    debit &&
    p.sens === 'facture' &&
    nommage.strict &&
    lue &&
    dateLue &&
    fenetreSure &&
    (etrangere ? bornes !== null && change !== null : montant)
  const fenetreConcordance =
    op.nature === 'carte' && etrangere ? FENETRE_SURE_DEVISE : fenetreDeConcordance(op.nature)
  const concordante =
    arete &&
    debit &&
    p.sens === 'facture' &&
    nommage.large &&
    (montant || origine !== null || change !== null) &&
    dans(e, fenetreConcordance)

  const enDevise = change ?? origine ?? pres ?? (etrangere ? (p.absolus[0] ?? null) : null)
  return {
    pieceId: p.piece.id,
    score,
    signaux,
    ecartJours: e,
    rapportChange: enDevise === null ? null : rapportDeChange(absOp, enDevise),
    arete,
    stricte,
    nomStrict: nommage.strict,
    fenetreSure,
    deviseLue: lue,
    dateLue,
    montantComparable:
      lue && (!etrangere || bornes !== null || o.origines.some((m) => m.devise === devise)),
    montantConcorde,
    sansBorne: etrangere && bornes === null,
    concordante,
    decisive: echeance || numero,
  }
}

/**
 * Évalue une paire (opération, pièce) : écart retenu (le plus petit sur la date de la pièce et
 * celles de ses reçus), signaux, score, taux effectif ; `arete` si la pièce peut être proposée
 * (signes compatibles, fenêtre large, score et un signal qui la désigne), `stricte` si elle
 * peut être sûre (débit et facture, fournisseur nommé au sens strict, montant égal, ou change
 * dans la bande pour une devise bornée, date et devise de la pièce lues, fenêtre sûre). Null si
 * elles ne se comparent pas : autre entité, date illisible. `bandes` : les bandes de change, celles
 * des justificatifs (`BORNES_CHANGE`) par défaut ; le règlement par débit passe celles du débit.
 */
export function evaluerJustificatif(
  op: OperationBancaire,
  piece: PieceJustificative,
  bandes: Readonly<Record<string, BorneChange>> = BORNES_CHANGE,
): EvaluationJustificatif | null {
  const o = preparerOperation(op)
  const p = preparerPiece(piece)
  const paire = o && p ? evaluer(o, p, bandes) : null
  return paire ? evaluation(paire) : null
}

/** Correspondance stricte : seule une paire stricte et unique dans les deux sens est sûre. */
export function correspondanceStricte(op: OperationBancaire, piece: PieceJustificative): boolean {
  return evaluerJustificatif(op, piece)?.stricte ?? false
}

const candidateDe = (x: Paire): CandidateJustificatif => ({
  pieceId: x.pieceId,
  score: x.score,
  signaux: x.signaux,
  ecartJours: x.ecartJours,
  rapportChange: x.rapportChange,
})

const evaluation = (x: Paire): EvaluationJustificatif => ({
  ...candidateDe(x),
  arete: x.arete,
  stricte: x.stricte,
})

/** Par score, puis plus petit écart, puis identifiant. */
const ordreCandidates = (a: Paire, b: Paire) =>
  b.score - a.score ||
  Math.abs(a.ecartJours) - Math.abs(b.ecartJours) ||
  comparer(a.pieceId, b.pieceId)

// ---------------------------------------------------------------------------------------------
// Rapprochement
// ---------------------------------------------------------------------------------------------

/**
 * Issue de chaque opération lue. L'unicité se compte sur les paires concordantes, strictes ou
 * non (débit et facture, même montant, fournisseur nommé au moins au sens large, écart dans la
 * fenêtre de concordance de la nature : celle de la carte, sinon la fenêtre large), sur toutes
 * les opérations lues (justifiées et hors fenêtre comprises) et toutes les pièces fournies,
 * exclusions humaines comprises : une exclusion ne rend jamais sûre une autre correspondance.
 * Une opération désigne sa seule pièce concordante, ou, parmi plusieurs, la seule qu'un signal
 * départage (échéance d'un prélèvement, numéro de la pièce) ; une pièce désigne son opération de
 * même. Pour chaque opération de la fenêtre de décision, dans cet ordre :
 * 1. hors champ : montant nul, rejet, virement interne, organisme sans facture, opération
 *    provisoire sans arête ;
 * 2. déjà justifiée chez la source : pièce reconnue si chacune désigne l'autre, la paire stricte,
 *    la lecture complète et la pièce non exclue ; sinon les candidates ;
 * 3. sûre : de même, et ni ventilée ni provisoire ;
 * 4. proposée : candidates (sans les pièces sûres ou reconnues ailleurs, ni exclues), avec le
 *    premier critère qui manque ;
 * 5. sinon crédit hors champ, débit à fournir.
 * Hors de la fenêtre : `hors_fenetre`. Une date illisible écarte la ligne, sans erreur ; une
 * référence ou un identifiant en double ne compte qu'une fois (la première ligne lisible). Sans
 * doublon, le résultat ne dépend pas de l'ordre des entrées.
 */
export function rapprocherJustificatifs(
  operations: readonly OperationAJustifier[],
  pieces: readonly PieceJustificative[],
  options: OptionsJustificatifs,
): ResultatJustificatifs {
  const ops: OperationPrete<OperationAJustifier>[] = []
  const refs = new Set<string>()
  for (const op of operations) {
    const o = preparerOperation(op)
    if (!o || refs.has(op.ref)) continue
    refs.add(op.ref)
    ops.push(o)
  }
  ops.sort((a, b) => a.jour - b.jour || comparer(a.op.ref, b.op.ref))

  const pcs: PiecePrete[] = []
  const ids = new Set<string>()
  for (const piece of pieces) {
    const p = preparerPiece(piece)
    if (!p || ids.has(piece.id)) continue
    ids.add(piece.id)
    pcs.push(p)
  }

  const exclusions = new Map<string, ReadonlySet<string>>()
  for (const [ref, pieceIds] of options.exclusions ?? []) exclusions.set(ref, new Set(pieceIds))
  const exclue = (ref: string, pieceId: string) => exclusions.get(ref)?.has(pieceId) ?? false

  // Toutes les paires candidates, et les paires concordantes dans les deux sens : elles font
  // l'unicité, strictes ou non (une facture d'une période voisine, une jumelle sans devise lue).
  const aretes = new Map<string, Paire[]>()
  const concordantesDeOperation = new Map<string, Paire[]>()
  const concordantesDePiece = new Map<string, { ref: string; paire: Paire }[]>()
  const piecesEnVue = new Set<string>()
  for (const o of ops) {
    const liste: Paire[] = []
    for (const p of pcs) {
      if (horsDePortee(o, p)) continue
      const x = evaluer(o, p)
      if (!x?.arete) continue
      liste.push(x)
      if (!exclue(o.op.ref, x.pieceId)) piecesEnVue.add(x.pieceId)
      if (x.concordante) {
        concordantesDeOperation.set(o.op.ref, [...(concordantesDeOperation.get(o.op.ref) ?? []), x])
        concordantesDePiece.set(x.pieceId, [
          ...(concordantesDePiece.get(x.pieceId) ?? []),
          { ref: o.op.ref, paire: x },
        ])
      }
    }
    aretes.set(o.op.ref, liste)
  }

  const fenetreLisible =
    numeroDuJour(options.decisionDu) !== null && numeroDuJour(options.au) !== null
  const aDecider = (jour: string) =>
    fenetreLisible && jour >= options.decisionDu && jour <= options.au

  /** La pièce que désigne l'opération : seule concordante, sinon seule à un signal qui départage. */
  const pieceDesignee = (ref: string): Paire | null =>
    designee(concordantesDeOperation.get(ref) ?? [], (x) => x)
  /** L'opération que désigne la pièce, de même. */
  const operationDesignee = (pieceId: string): string | null =>
    designee(concordantesDePiece.get(pieceId) ?? [], (x) => x.paire)?.ref ?? null

  /**
   * La pièce de l'opération quand chacune désigne l'autre (exclusions humaines comprises : une
   * exclusion ne rend jamais sûre une autre correspondance), que la paire est stricte, la lecture
   * complète et la pièce non exclue pour cette opération.
   */
  const seuleStricte = (ref: string): string | null => {
    const x = pieceDesignee(ref)
    if (!x || operationDesignee(x.pieceId) !== ref || !x.stricte) return null
    if (!options.complete || exclue(ref, x.pieceId)) return null
    return x.pieceId
  }

  const internes = options.nomsInternes.map(nomNormalise).filter((n) => n !== '')
  const sansFacture = LIBELLES_SANS_FACTURE.map(nomNormalise).filter((n) => n !== '')
  const deposants = PRESTATAIRES_DEPOSANT_LEURS_PIECES.map(nomNormalise).filter((n) => n !== '')
  const horsChamp = (o: OperationPrete<OperationAJustifier>): MotifHorsChampJustificatif | null => {
    const op = o.op
    if (op.montantCts === 0) return 'montant_nul'
    if (estRejet(normaliserLibelle(op.libelle))) return 'rejet'
    if (
      (op.montantCts > 0 || op.nature === 'virement') &&
      internes.some((n) => contient(o.bancaire, n))
    )
      return 'interne'
    if (
      sansFacture.some((n) => contient(o.bancaire, n)) ||
      REFERENCES_SANS_FACTURE.some((r) => r.test(o.bancaire))
    )
      return 'organisme'
    if (deposants.some((n) => contient(o.bancaire, n))) return 'depose_par_prestataire'
    const enVue = (aretes.get(op.ref) ?? []).some((x) => !exclue(op.ref, x.pieceId))
    if (op.provisoire && !enVue) return 'non_realisee'
    if (op.validee && op.justificatifs === 0 && !enVue) return 'validee_sans_piece'
    return null
  }

  // Première passe : hors fenêtre, hors champ, sûres, pièces reconnues.
  const resultats: (IssueJustificatif | null)[] = ops.map(() => null)
  const reconnues = new Map<string, string>()
  const attribuees = new Map<string, string>()
  ops.forEach((o, i) => {
    const ref = o.op.ref
    if (!aDecider(o.op.date)) {
      resultats[i] = { issue: 'hors_fenetre' }
      return
    }
    const motif = horsChamp(o)
    if (motif) {
      resultats[i] = { issue: 'hors_champ', motif }
      return
    }
    const seule = seuleStricte(ref)
    if (o.op.justificatifs > 0) {
      if (seule) {
        reconnues.set(ref, seule)
        attribuees.set(seule, ref)
      }
      return
    }
    if (seule && !o.op.ventilee && !o.op.provisoire && !o.op.validee) {
      const x = (aretes.get(ref) ?? []).find((a) => a.pieceId === seule)!
      resultats[i] = {
        issue: 'sure',
        pieceId: seule,
        signaux: x.signaux,
        ecartJours: x.ecartJours,
        rapportChange: x.rapportChange,
      }
      attribuees.set(seule, ref)
    }
  })

  // Seconde passe : justifiées, proposées, à fournir.
  ops.forEach((o, i) => {
    if (resultats[i]) return
    const ref = o.op.ref
    const candidates = (aretes.get(ref) ?? [])
      .filter((x) => !exclue(ref, x.pieceId) && (attribuees.get(x.pieceId) ?? ref) === ref)
      .sort(ordreCandidates)
    const gardees = candidates.slice(0, MAX_CANDIDATES).map(candidateDe)
    if (o.op.justificatifs > 0) {
      resultats[i] = {
        issue: 'justifiee',
        pieceReconnue: reconnues.get(ref) ?? null,
        candidates: gardees,
      }
    } else if (candidates[0]) {
      resultats[i] = {
        issue: 'proposee',
        candidates: gardees,
        motif: motifDeProposition(o.op, candidates[0], {
          concordantes: (concordantesDeOperation.get(ref) ?? []).length,
          designee: pieceDesignee(ref),
          designeeEnRetour: (pieceId) => operationDesignee(pieceId) === ref,
          exclue: (pieceId) => exclue(ref, pieceId),
          complete: options.complete,
        }),
      }
    } else {
      resultats[i] =
        o.op.montantCts > 0 ? { issue: 'hors_champ', motif: 'credit' } : { issue: 'a_fournir' }
    }
  })

  const issues = new Map<string, IssueJustificatif>()
  ops.forEach((o, i) => issues.set(o.op.ref, resultats[i]!))
  const piecesSansOperation = pcs
    .filter((p) => aDecider(p.piece.jour) && !piecesEnVue.has(p.piece.id))
    .sort((a, b) => a.jour - b.jour || comparer(a.piece.id, b.piece.id))
    .map((p) => p.piece.id)
  return { issues, piecesSansOperation }
}

/**
 * Une paire désignée parmi des paires concordantes : la seule, ou la seule à un signal qui
 * départage (échéance d'un prélèvement, numéro de la pièce) ; sinon aucune.
 */
function designee<T>(liste: readonly T[], paire: (x: T) => Paire): T | null {
  if (liste.length === 1) return liste[0]!
  const decisives = liste.filter((x) => paire(x).decisive)
  return decisives.length === 1 ? decisives[0]! : null
}

/** Premier critère de la paire qui manque pour être stricte (hors unicité et opération). */
function critereManquant(x: Paire): MotifPropositionJustificatif | null {
  if (!x.nomStrict) return 'fournisseur_non_nomme'
  if (x.montantComparable && !x.montantConcorde) return 'montant_different'
  if (!x.dateLue) return 'date_non_lue'
  if (!x.fenetreSure) return 'hors_fenetre_sure'
  if (!x.deviseLue) return 'devise_non_lue'
  if (x.sansBorne) return 'devise_sans_borne'
  return null
}

/**
 * Premier critère qui manque pour que l'opération soit sûre : l'unicité (plusieurs pièces
 * concordent sans que rien les départage, ou la pièce désignée l'est aussi par une autre
 * opération), puis, pour la pièce désignée de part et d'autre (sinon la meilleure candidate), le
 * fournisseur, le montant, la date, la fenêtre, la devise, puis l'opération elle-même, et enfin
 * le sens (avoir).
 */
function motifDeProposition(
  op: OperationAJustifier,
  meilleure: Paire,
  c: {
    concordantes: number
    designee: Paire | null
    designeeEnRetour: (pieceId: string) => boolean
    exclue: (pieceId: string) => boolean
    complete: boolean
  },
): MotifPropositionJustificatif {
  const motifOperation = (): MotifPropositionJustificatif | null =>
    op.ventilee
      ? 'ventilee'
      : op.provisoire
        ? 'provisoire'
        : op.validee
          ? 'validee'
          : !c.complete
            ? 'lecture_incomplete'
            : null
  const x = c.designee
  if (c.concordantes >= 2 && !x) return 'plusieurs_pieces'
  if (x) {
    if (!c.designeeEnRetour(x.pieceId)) return 'piece_disputee'
    if (!c.exclue(x.pieceId)) {
      const m = critereManquant(x) ?? motifOperation()
      if (m) return m
    }
  }
  // Tout concorde sauf l'opération elle-même, ou le sens : un crédit contre un avoir.
  return critereManquant(meilleure) ?? motifOperation() ?? 'avoir'
}
